/* =====================================================================
   ArchitectureFlow.tsx
   ---------------------------------------------------------------------
   A self-contained React Flow (@xyflow/react) renderer for the
   `Architecture` shape. Containers become nested parent/group nodes;
   resources become custom child nodes with an icon; edges are styled by
   kind (traffic / peering / management / encryption).

   React Flow does not auto-layout nested groups, so `layoutContainer()`
   does a simple bottom-up pass: each container measures its children
   (resources in a wrapped grid, then sub-containers stacked) and returns
   its size; positions are assigned relative to the parent.

   This version adds the behaviours from the Claude design artifact:
   - an Architecture (high-level) / Detailed view toggle,
   - per-container expand/collapse (click the chevron in a header),
   - a refit pass (`FitOnChange`) so the diagram fills the whole pane once
     nodes are measured, instead of only the lower half.
   ===================================================================== */

import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  MarkerType,
  useReactFlow,
  useNodesInitialized,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { Architecture, EdgeKind, NodeCategory } from "./types";
import { resolveCloudIcon } from "./cloudIcons";
import { withOnPremises } from "./onPremises";
import type { IaCContext } from "../../api";
import { exportFlowToPdf } from "@/lib/exportFlowToPdf";
import { Book, BookOpenText } from "lucide-react";

interface FlowProps {
  architecture: Architecture;
  onRebuild?: () => void;
  rebuilding?: boolean;
  stale?: boolean;
  /** The service's placement: what stays on-site is drawn with its links back (onPremises.ts). */
  context?: IaCContext | null;
  /** The project's Terraform, to tell what the engineer chose to build in the cloud after all. */
  terraform?: string;
}

/* ---- category + edge palettes (mirror the CSS variables) ---- */
const CATEGORY_COLOR: Record<NodeCategory, string> = {
  compute: "var(--cat-compute, #5b6cc4)",
  database: "var(--cat-database, #2f9e7e)",
  network: "var(--cat-network, #9166c0)",
  security: "var(--cat-security, #c08a3e)",
};

const EDGE_COLOR: Record<EdgeKind, string> = {
  traffic: "var(--edge-traffic, #4a63b0)",
  peering: "var(--edge-peering, #6b7280)",
  management: "var(--edge-management, #8a8f98)",
  encryption: "var(--edge-encryption, #c08a3e)",
  "site-link": "var(--edge-site-link, #d97706)",
};

const EDGE_DASH: Record<EdgeKind, string | undefined> = {
  traffic: undefined,
  peering: undefined,
  management: "6 4",
  encryption: "2 4",
  "site-link": "8 3",
};
/* The site is not in the cloud: its area has its own colour, whatever its depth. */
const SITE_HUE = "var(--edge-site-link, #d97706)";

/* Pastel fill so nested areas are easy to tell apart. Shading is driven by
   nesting DEPTH, not by container kind, so it works for any cloud vendor (AWS
   account / Azure subscription / GCP project all nest the same way) and scales
   to arbitrarily many/deep areas as the Terraform grows. Each level gets a
   distinct translucent pastel; because the tints are translucent they also
   stack, reinforcing depth, and adapt to light/dark themes. The governance
   "band" keeps a fixed amber accent regardless of depth. */
const DEPTH_HUES = [
  "var(--cat-compute, #5b6cc4)",   // depth 1
  "var(--cat-network, #9166c0)",   // depth 2
  "var(--cat-database, #2f9e7e)",  // depth 3
  "var(--edge-peering, #6b7280)",  // depth 4
  "var(--cat-security, #c08a3e)",  // depth 5+
];
const BAND_HUE = "var(--cat-security, #c08a3e)";

function containerTint(kind: string, depth: number): string {
  if (depth <= 0) return "var(--surface, #fff)"; // outermost (account) is the card surface
  if (kind === "band") return `color-mix(in oklch, ${BAND_HUE} 9%, transparent)`;
  if (kind === "on-premises") return `color-mix(in oklch, ${SITE_HUE} 7%, var(--surface, #fff))`;
  if (kind === "on-premises-group") return `color-mix(in oklch, ${SITE_HUE} 11%, transparent)`;
  const hue = DEPTH_HUES[(depth - 1) % DEPTH_HUES.length];
  return `color-mix(in oklch, ${hue} ${Math.min(6 + depth, 11)}%, transparent)`;
}

/** Nesting depth of a container (root/account = 0). */
function containerDepth(arch: Architecture, id: string): number {
  let depth = 0;
  let cur = arch.containers.find((c) => c.id === id);
  const seen = new Set<string>();
  while (cur && cur.parentId && !seen.has(cur.id)) {
    seen.add(cur.id);
    depth += 1;
    cur = arch.containers.find((c) => c.id === cur!.parentId);
  }
  return depth;
}

/* ---- layout constants ---- */
const NODE_W = 168;
const NODE_H = 56;
const GAP = 12;
const PAD_X = 12;
const HEADER_H = 34;
const BAND_COLS = 4;
const ROOT_WIDTH = 1180;
// Minimum width a sub-container needs before we place siblings beside it instead
// of stacking them. Keeps the diagram landscape (fills a wide pane) rather than
// a tall narrow strip that fitView letterboxes.
const MIN_CHILD_W = 420;
const MAX_RES_COLS = 4;

type Mode = "detailed" | "architecture";
interface LayoutOpts {
  mode: Mode;
  collapsed: Set<string>;
}
type LaidOut = { nodes: RFNode[]; size: { w: number; h: number } };

/** Count resource nodes anywhere beneath a container (for the count badge). */
function countDescendantResources(arch: Architecture, containerId: string): number {
  let n = arch.nodes.filter((x) => x.containerId === containerId).length;
  for (const c of arch.containers.filter((c) => c.parentId === containerId)) {
    n += countDescendantResources(arch, c.id);
  }
  return n;
}

/**
 * Recursively lays out a container and everything inside it.
 * Returns React Flow nodes with positions RELATIVE to their parent.
 * Honours `collapsed` (header only) and `mode` (architecture hides resources).
 */
function layoutContainer(
  arch: Architecture,
  containerId: string,
  availableWidth: number,
  opts: LayoutOpts,
): LaidOut {
  const container = arch.containers.find((c) => c.id === containerId)!;

  // Collapsed → render just the header band, no children.
  if (opts.collapsed.has(containerId)) {
    return { nodes: [], size: { w: availableWidth, h: HEADER_H + 8 } };
  }

  const childContainers = arch.containers.filter((c) => c.parentId === containerId);
  // Architecture (high-level) view hides individual resources, keeping the structure.
  const childNodes = opts.mode === "architecture"
    ? []
    : arch.nodes.filter((n) => n.containerId === containerId);

  const innerWidth = availableWidth - PAD_X * 2;
  const cols = container.kind === "band"
    ? BAND_COLS
    : Math.max(1, Math.min(MAX_RES_COLS, Math.floor((innerWidth + GAP) / (NODE_W + GAP))));
  const colW = (innerWidth - GAP * (cols - 1)) / cols;

  const out: RFNode[] = [];
  let cursorY = HEADER_H;

  // 1) resource nodes in a wrapped grid
  childNodes.forEach((n, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    out.push({
      id: n.id,
      type: "resource",
      parentId: containerId,
      extent: "parent",
      position: { x: PAD_X + col * (colW + GAP), y: cursorY + row * (NODE_H + GAP) },
      data: { node: n },
      style: { width: colW, height: NODE_H },
    });
  });
  if (childNodes.length) {
    const rows = Math.ceil(childNodes.length / cols);
    cursorY += rows * (NODE_H + GAP);
  }

  // 2) sub-containers, packed into a grid (shelf packing) so siblings sit side
  //    by side when there's room — keeps the overall diagram landscape instead
  //    of one tall vertical column. Single-child chains stay full-width.
  if (childContainers.length) {
    // Only spread siblings side-by-side when they're leaf containers (e.g. subnets
    // holding resources). Containers that nest further stay full-width so their own
    // children get room to spread.
    const childrenAreLeaves = childContainers.every(
      (c) => !arch.containers.some((cc) => cc.parentId === c.id),
    );
    const ccols = childrenAreLeaves
      ? Math.max(1, Math.min(childContainers.length, Math.floor((innerWidth + GAP) / (MIN_CHILD_W + GAP))))
      : 1;
    const childW = (innerWidth - GAP * (ccols - 1)) / ccols;
    const laidChildren = childContainers.map((c) => layoutContainer(arch, c.id, childW, opts));

    let colIdx = 0;
    let rowMaxH = 0;
    childContainers.forEach((child, k) => {
      const laid = laidChildren[k];
      out.push({
        id: child.id,
        type: "container",
        parentId: containerId,
        extent: "parent",
        position: { x: PAD_X + colIdx * (childW + GAP), y: cursorY },
        data: { container: child },
        style: { width: childW, height: laid.size.h },
      });
      out.push(...laid.nodes);
      rowMaxH = Math.max(rowMaxH, laid.size.h);
      colIdx += 1;
      if (colIdx === ccols) {
        cursorY += rowMaxH + GAP;
        colIdx = 0;
        rowMaxH = 0;
      }
    });
    if (colIdx !== 0) cursorY += rowMaxH + GAP; // close a partial final row
  }

  if (!childNodes.length && !childContainers.length) cursorY += 6;
  return { nodes: out, size: { w: availableWidth, h: cursorY + PAD_X } };
}

/** Exported for its tests: the nodes and edges React Flow draws, for one view. */
export function buildGraph(arch: Architecture, opts: LayoutOpts) {
  const roots = arch.containers.filter((c) => !c.parentId);
  const nodes: RFNode[] = [];
  let y = 0;
  for (const root of roots) {
    const laid = layoutContainer(arch, root.id, ROOT_WIDTH, opts);
    nodes.push({
      id: root.id,
      type: "container",
      position: { x: 0, y },
      data: { container: root },
      style: { width: ROOT_WIDTH, height: laid.size.h },
    });
    nodes.push(...laid.nodes);
    y += laid.size.h + GAP * 2;
  }

  // Annotate container nodes with UI state (collapse affordance + resource count).
  for (const node of nodes) {
    if (node.type !== "container") continue;
    const id = node.id;
    const hasChildren =
      arch.containers.some((c) => c.parentId === id) || arch.nodes.some((n) => n.containerId === id);
    node.data = {
      ...(node.data as any),
      collapsed: opts.collapsed.has(id),
      hasChildren,
      resourceCount: countDescendantResources(arch, id),
      showCount: opts.mode === "architecture" || opts.collapsed.has(id),
      depth: containerDepth(arch, id),
    };
  }

  // Only keep edges whose endpoints are actually rendered (collapse / architecture
  // hides nodes; React Flow would otherwise warn about dangling edges).
  const rendered = new Set(nodes.map((n) => n.id));
  // A link back to the site must show in the high-level view too, where the gateway and the
  // on-site systems are hidden: each end moves up to the nearest area that is drawn. Several
  // systems then share one line, so it is drawn once, without the per-system label.
  const parentOf = (id: string): string | undefined =>
    arch.nodes.find((n) => n.id === id)?.containerId ?? arch.containers.find((c) => c.id === id)?.parentId;
  const drawnEnd = (id: string): string | undefined => {
    let cur: string | undefined = id;
    for (let hops = 0; cur && !rendered.has(cur) && hops < 50; hops++) cur = parentOf(cur);
    return cur && rendered.has(cur) ? cur : undefined;
  };
  const seenSiteLinks = new Set<string>();
  const shown = arch.edges.flatMap((e) => {
    if (e.kind !== "site-link") return [e];
    const source = drawnEnd(e.source);
    const target = drawnEnd(e.target);
    if (!source || !target || source === target) return [];
    const key = `${source}->${target}`;
    if (seenSiteLinks.has(key)) return [];
    seenSiteLinks.add(key);
    const moved = source !== e.source || target !== e.target;
    return [{ ...e, source, target, label: moved ? "Site link" : e.label }];
  });
  const edges: RFEdge[] = shown
    .filter((e) => rendered.has(e.source) && rendered.has(e.target))
    .map((e, i) => ({
      id: `${e.source}->${e.target}-${i}`,
      source: e.source,
      target: e.target,
      label: e.label,
      type: "smoothstep",
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_COLOR[e.kind] },
      markerStart:
        e.kind === "peering" ? { type: MarkerType.ArrowClosed, color: EDGE_COLOR[e.kind] } : undefined,
      style: {
        stroke: EDGE_COLOR[e.kind],
        strokeWidth: e.kind === "traffic" || e.kind === "peering" || e.kind === "site-link" ? 2 : 1.5,
        strokeDasharray: EDGE_DASH[e.kind],
      },
      data: { kind: e.kind },
    }));

  return { nodes, edges };
}

/* ---- custom nodes ---- */
function ContainerNode({ id, data }: NodeProps) {
  const c = (data as any).container;
  const dashed = c.kind === "band";
  const collapsed = (data as any).collapsed;
  const hasChildren = (data as any).hasChildren;
  const showCount = (data as any).showCount;
  const count = (data as any).resourceCount;
  const onToggle = (data as any).onToggle as ((id: string) => void) | undefined;
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        border: `1px ${dashed ? "dashed" : "solid"} var(--line, #d7dae0)`,
        borderRadius: 12,
        background: containerTint(c.kind, (data as any).depth ?? 0),
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px" }}>
        {hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle?.(id); }}
            title={collapsed ? "Expand" : "Collapse"}
            aria-label={collapsed ? "Expand" : "Collapse"}
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              color: "var(--ink-3,#888)", fontSize: 11, lineHeight: 1, padding: 0, width: 14,
            }}
          >
            {collapsed ? "▸" : "▾"}
          </button>
        )}
        <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--ink-3,#888)" }}>
          {c.kind === "on-premises" ? "on-premises" : c.kind === "on-premises-group" ? "on-site" : c.kind.replace("subnet-", "")}
        </span>
        <span style={{ fontSize: 13, fontWeight: 650, color: "var(--ink,#222)" }}>{c.label}</span>
        {c.sublabel && (
          <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--ink-3,#888)" }}>{c.sublabel}</span>
        )}
        {showCount && count > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, color: "var(--ink-3,#888)", background: "color-mix(in oklch, var(--ink-3,#888) 12%, transparent)", borderRadius: 9999, padding: "1px 8px" }}>
            {count}
          </span>
        )}
      </div>
    </div>
  );
}

function ResourceNode({ data }: NodeProps) {
  const n = (data as any).node;
  const color = CATEGORY_COLOR[n.category as NodeCategory];
  const [logoFailed, setLogoFailed] = useState(false);
  // An on-site system is not a cloud service: no vendor logo, whatever its words say.
  const logo = logoFailed || n.onPremises ? null : resolveCloudIcon(n);
  return (
    <div
      title={n.service}
      style={{
        display: "flex", alignItems: "center", gap: 9, width: "100%", height: "100%",
        padding: "8px 10px", border: "1px solid var(--line,#d7dae0)", borderRadius: 9,
        background: "var(--surface,#fff)", boxSizing: "border-box",
      }}
    >
      <span
        style={{
          width: 28, height: 28, borderRadius: 7, flex: "0 0 auto",
          display: "grid", placeItems: "center",
          color,
          // Neutralise the coloured tile when a full-colour vendor logo shows.
          background: logo ? "transparent" : `color-mix(in oklch, ${color} 13%, transparent)`,
        }}
      >
        {logo ? (
          <img
            src={logo}
            alt=""
            onError={() => setLogoFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <ServiceIcon icon={n.icon} />
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--ink,#222)" }}>{n.label}</span>
        {n.sublabel && (
          <span style={{ display: "block", fontFamily: "monospace", fontSize: 10, color: "var(--ink-3,#888)" }}>
            {n.sublabel}
          </span>
        )}
      </span>
    </div>
  );
}

/** Monoline glyph fallback when no vendor logo resolves (unmapped service). */
function ServiceIcon({ icon }: { icon?: string }) {
  return <span style={{ fontSize: 11 }}>{(icon ?? "?").slice(0, 1).toUpperCase()}</span>;
}

const nodeTypes = { container: ContainerNode, resource: ResourceNode };

/**
 * Refit the view to fill the pane:
 *  - once nodes are measured and whenever the layout changes (signature), and
 *  - whenever the container resizes. The Overview lives in a flex/tab panel whose
 *    final size settles AFTER mount (and changes when you switch tabs), so a
 *    one-shot fit lands against a stale viewport and parks the diagram small/low.
 *    A ResizeObserver re-fits once the real size is known.
 */
function FitOnChange({ signature, containerRef }: { signature: string; containerRef: React.RefObject<HTMLElement | null> }) {
  const initialized = useNodesInitialized();
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (initialized) fitView({ padding: 0.12, duration: 250 });
  }, [initialized, signature, fitView]);

  // Mount-time deferred fit: the pane's final size can settle a frame or two after
  // mount (flex + deployment panel below), and React Flow does not auto-fit on
  // resize — so fit again once the layout has flushed.
  useEffect(() => {
    let r1 = 0, r2 = 0;
    r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => fitView({ padding: 0.12 })); });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [fitView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (el.clientHeight > 0 && el.clientWidth > 0) fitView({ padding: 0.12 });
      });
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [containerRef, fitView]);

  return null;
}

/* ---- the component ---- */
function Flow({ architecture: derived, onRebuild, rebuilding, stale, context, terraform = "" }: FlowProps) {
  // The cloud from the Terraform, plus what stays on-site and the links back to it.
  const architecture = useMemo(() => withOnPremises(derived, context, terraform), [derived, context, terraform]);
  const [mode, setMode] = useState<Mode>("detailed");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { nodes, edges } = useMemo(() => {
    const g = buildGraph(architecture, { mode, collapsed });
    g.nodes.forEach((n) => {
      if (n.type === "container") n.data = { ...(n.data as any), onToggle: toggle };
    });
    return g;
  }, [architecture, mode, collapsed, toggle]);

  const signature = `${mode}|${[...collapsed].sort().join(",")}|${nodes.length}`;

  // Segmented-toggle button classes — mirrors the app's ViewSwitcher (Objects/Services).
  const segBtn = (active: boolean) =>
    `flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] transition-colors ${
      active ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground"
    }`;

  const wrapRef = useRef<HTMLDivElement>(null);
  const { getNodes } = useReactFlow();
  const [exporting, setExporting] = useState(false);

  // Export the whole diagram to a PDF (shared util — captures at high res, sizes
  // the page to the diagram, and prompts for a save location where supported).
  const downloadPdf = useCallback(async () => {
    const ns = getNodes();
    const viewport = wrapRef.current?.querySelector(".react-flow__viewport") as HTMLElement | null;
    if (!ns.length || !viewport) return;
    setExporting(true);
    try {
      await exportFlowToPdf({ nodes: ns, viewportEl: viewport, filename: `architecture-diagram-${mode}.pdf` });
    } catch (e) {
      console.error("Architecture PDF export failed", e);
    } finally {
      setExporting(false);
    }
  }, [getNodes, mode]);

  return (
    <div ref={wrapRef} style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        minZoom={0.05}
        nodesDraggable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Panel position="top-left">
          <div className="flex items-center gap-2">
            {/* Architecture / Detailed — segmented toggle in the app's view-switcher style */}
            <div className="inline-flex items-center overflow-hidden rounded-md border border-border bg-background shadow-sm">
              <button className={`${segBtn(mode === "architecture")} border-r border-border`} onClick={() => setMode("architecture")} title="High-level architecture">
                <Book size={13} />
                <span>Architecture</span>
              </button>
              <button className={segBtn(mode === "detailed")} onClick={() => setMode("detailed")} title="Detailed — every resource">
                <BookOpenText size={13} />
                <span>Detailed</span>
              </button>
            </div>
            {stale && (
              <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                Terraform changed
              </span>
            )}
            {collapsed.size > 0 && (
              <button className="rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" onClick={() => setCollapsed(new Set())} title="Expand all containers">
                Expand all
              </button>
            )}
            {/* Download PDF — icon button (same download icon as the controls panel) + tooltip */}
            <button
              className="flex items-center justify-center rounded-md border border-border bg-background p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
              onClick={downloadPdf}
              disabled={exporting}
              title="Download PDF"
              aria-label="Download PDF"
            >
              {exporting ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              )}
            </button>
            {/* Rebuild — to the right of Download; same rebuild icon as the controls panel + tooltip */}
            {onRebuild && (
              <button
                className="flex items-center justify-center rounded-md border border-border bg-background p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
                onClick={onRebuild}
                disabled={rebuilding}
                title="Rebuild diagram"
                aria-label="Rebuild diagram"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={rebuilding ? "animate-spin" : ""}><path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
              </button>
            )}
          </div>
        </Panel>
        <FitOnChange signature={signature} containerRef={wrapRef} />
        <Background gap={20} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
}

export function ArchitectureFlow({ architecture, onRebuild, rebuilding, stale, context, terraform }: FlowProps) {
  return (
    <ReactFlowProvider>
      <Flow architecture={architecture} onRebuild={onRebuild} rebuilding={rebuilding} stale={stale} context={context} terraform={terraform} />
    </ReactFlowProvider>
  );
}

export default ArchitectureFlow;
